/**
 * Spike test room. Not art: a greybox to measure the stack.
 * Wet stone floor, five monoliths on an arc with placeholder art,
 * a torii stand-in, two coloured lights and range fog into the void.
 */
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import * as THREE from 'three/webgpu'
import { color, fog } from 'three/tsl'
import { useThree } from '@react-three/fiber/webgpu'
import {
  createLacquerMaterial,
  createMonolithBodyMaterial,
  createPillarMaterial,
  createPlaceholderArtMaterial,
  createWetStoneMaterial,
} from '../tsl/materials'
import { palette } from '../tsl/palette'
import { voidFogFactor } from '../tsl/atmosphere'
import { flags } from '../config/flags'

type SceneWithFog = THREE.Scene & { fogNode: unknown }

function Atmosphere() {
  const scene = useThree((s) => s.scene) as SceneWithFog
  useEffect(() => {
    scene.background = new THREE.Color(palette.void)
    scene.fogNode = fog(color(palette.void), voidFogFactor())
    return () => {
      scene.background = null
      scene.fogNode = null
    }
  }, [scene])
  return null
}

function Floor() {
  const material = useMemo(createWetStoneMaterial, [])
  useEffect(() => () => material.dispose(), [material])
  return (
    <mesh rotation-x={-Math.PI / 2} material={material}>
      <planeGeometry args={[80, 80, 1, 1]} />
    </mesh>
  )
}

const MONOLITH = { w: 1.6, h: 3.2, d: 0.24 }

function Monolith({ seed, position, rotationY }: { seed: number; position: [number, number, number]; rotationY: number }) {
  const body = useMemo(createMonolithBodyMaterial, [])
  const art = useMemo(() => createPlaceholderArtMaterial(seed), [seed])
  useEffect(
    () => () => {
      body.dispose()
      art.dispose()
    },
    [body, art],
  )
  return (
    <group position={position} rotation-y={rotationY}>
      <mesh position-y={MONOLITH.h / 2 + 0.15} material={body}>
        <boxGeometry args={[MONOLITH.w, MONOLITH.h, MONOLITH.d]} />
      </mesh>
      <mesh position={[0, MONOLITH.h / 2 + 0.15, MONOLITH.d / 2 + 0.002]} material={art}>
        <planeGeometry args={[MONOLITH.w - 0.16, MONOLITH.h - 0.16]} />
      </mesh>
      {/* plinth */}
      <mesh position-y={0.075} material={body}>
        <boxGeometry args={[MONOLITH.w + 0.3, 0.15, MONOLITH.d + 0.3]} />
      </mesh>
    </group>
  )
}

function Monoliths() {
  const items = useMemo(() => {
    const count = 5
    const radius = 7
    const spread = Math.PI * 0.55
    return Array.from({ length: count }, (_, i) => {
      const t = i / (count - 1) - 0.5
      const a = t * spread
      return {
        seed: i + 1,
        position: [Math.sin(a) * radius, 0, -Math.cos(a) * radius + 3] as [number, number, number],
        rotationY: -a,
      }
    })
  }, [])
  return (
    <>
      {items.map((m) => (
        <Monolith key={m.seed} {...m} />
      ))}
    </>
  )
}

function Torii() {
  const lacquer = useMemo(createLacquerMaterial, [])
  useEffect(() => () => lacquer.dispose(), [lacquer])
  return (
    <group position={[0, 0, -9]}>
      {[-1.9, 1.9].map((x) => (
        <mesh key={x} position={[x, 2.1, 0]} material={lacquer}>
          <cylinderGeometry args={[0.17, 0.2, 4.2, 24]} />
        </mesh>
      ))}
      <mesh position={[0, 4.35, 0]} material={lacquer}>
        <boxGeometry args={[5.4, 0.28, 0.42]} />
      </mesh>
      <mesh position={[0, 3.65, 0]} material={lacquer}>
        <boxGeometry args={[4.5, 0.18, 0.22]} />
      </mesh>
    </group>
  )
}

/** ?stress=N: N instanced pillars on rings behind the arc. One draw call, lots of fill. */
function StressPillars({ count }: { count: number }) {
  const ref = useRef<THREE.InstancedMesh>(null)
  const material = useMemo(createPillarMaterial, [])
  const geometry = useMemo(() => new THREE.CylinderGeometry(0.12, 0.12, 1, 10).translate(0, 0.5, 0), [])
  useEffect(
    () => () => {
      material.dispose()
      geometry.dispose()
    },
    [material, geometry],
  )

  useLayoutEffect(() => {
    const mesh = ref.current
    if (!mesh) return
    const m = new THREE.Matrix4()
    const q = new THREE.Quaternion()
    const s = new THREE.Vector3()
    const p = new THREE.Vector3()
    for (let i = 0; i < count; i++) {
      const ring = Math.floor(i / 60)
      const a = (i % 60) / 60 * Math.PI * 2 + ring * 0.37
      const r = 12 + ring * 2.5
      const h = 2 + ((i * 7919) % 100) / 100 * 6
      p.set(Math.cos(a) * r, 0, Math.sin(a) * r - 4)
      s.set(1, h, 1)
      m.compose(p, q, s)
      mesh.setMatrixAt(i, m)
    }
    mesh.instanceMatrix.needsUpdate = true
  }, [count])

  return <instancedMesh ref={ref} args={[geometry, material, count]} frustumCulled={false} />
}

export function TestRoom() {
  return (
    <>
      <Atmosphere />
      <hemisphereLight args={['#1a2030', '#000000', 0.25]} />
      <pointLight position={[0, 3.2, -8]} color={palette.crimson} intensity={40} distance={18} decay={2} />
      <pointLight position={[-4, 2.5, 4]} color={palette.teal} intensity={18} distance={14} decay={2} />
      <Floor />
      <Monoliths />
      <Torii />
      {flags.stress > 0 && <StressPillars count={flags.stress} />}
    </>
  )
}
